import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getMisPedidos, getAhorroAcumulado, getKitsConProductos } from '../../lib/portalClienteApi'

const ESTADO_BADGE = {
  pendiente_pago: { bg: '#fef3c7', color: '#92400e', label: 'Pendiente' },
  programada:     { bg: '#e6fafc', color: '#0f7480', label: 'Confirmada' },
  pagado:         { bg: '#e6fafc', color: '#0f7480', label: 'Confirmada' },
  en_ruta:        { bg: '#dbeafe', color: '#1e40af', label: 'En camino' },
  entregado:      { bg: '#d1fae5', color: '#065f46', label: 'Entregado' },
  cancelado:      { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
}

const TIPO_LABEL = {
  compra_unica:           { titulo: 'Kit · Compra única',  sub: 'Pedido puntual' },
  productos_sueltos:      { titulo: 'Tienda',              sub: 'Productos sueltos' },
  adicional_suscripcion:  { titulo: 'Suscripción',         sub: 'Adicional al kit' },
  suscripcion:            { titulo: 'Suscripción',         sub: 'Mensual' },
}

function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

export default function Pedidos() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [pedidos, setPedidos] = useState([])
  const [ahorro, setAhorro] = useState(null)
  const [productosFotos, setProductosFotos] = useState({})  // sku → imagen_url
  const [kitsMap, setKitsMap] = useState({})                // codigo → kit con productos
  const [filtro, setFiltro] = useState('todos')  // todos | suscripciones | sueltos
  const [cargando, setCargando] = useState(true)

  useEffect(() => { if (user?.id) cargar() }, [user?.id])

  async function cargar() {
    setCargando(true)
    const [peds, ah, kits] = await Promise.all([
      getMisPedidos(50),
      getAhorroAcumulado(user.id),
      getKitsConProductos(),
    ])
    setPedidos(peds)
    setAhorro(ah)

    // Mapa de kits por código
    const km = {}
    ;(kits || []).forEach(k => { km[k.codigo] = k })
    setKitsMap(km)

    // Recolectar todos los SKUs (de items de pedidos sueltos + de kits que aparecen en pedidos)
    const skus = new Set()
    for (const p of peds) {
      if (Array.isArray(p.items)) p.items.forEach(it => it.sku && skus.add(it.sku))
      if (p.kit_codigo && km[p.kit_codigo]) {
        ;(km[p.kit_codigo].productos || []).forEach(it => it.sku && skus.add(it.sku))
      }
    }
    if (skus.size > 0) {
      const { data } = await supabase
        .from('productos')
        .select('sku, imagen_url')
        .in('sku', [...skus])
      const fmap = {}
      ;(data || []).forEach(p => { if (p.imagen_url) fmap[p.sku] = p.imagen_url })
      setProductosFotos(fmap)
    }
    setCargando(false)
  }

  // Helper: obtener los SKUs (max 4) para mostrar miniatura de un pedido
  function getMiniaturas(p) {
    let skus = []
    if (Array.isArray(p.items) && p.items.length > 0) {
      skus = p.items.map(it => it.sku).filter(Boolean)
    } else if (p.kit_codigo && kitsMap[p.kit_codigo]) {
      skus = (kitsMap[p.kit_codigo].productos || []).map(it => it.sku).filter(Boolean)
    }
    return skus.slice(0, 4)
  }

  const filtrados = useMemo(() => {
    if (filtro === 'todos') return pedidos
    if (filtro === 'suscripciones') return pedidos.filter(p => p.tipo === 'suscripcion' || p.tipo === 'compra_unica')
    if (filtro === 'sueltos') return pedidos.filter(p => p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion')
    return pedidos
  }, [pedidos, filtro])

  // KPIs
  const totalCount = pedidos.length
  const totalAcumulado = pedidos.filter(p => p.estado === 'entregado' || p.estado === 'pagado' || p.estado === 'programada').reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const pendienteMonto = pedidos.filter(p => p.estado === 'pendiente_pago').reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const pendienteCount = pedidos.filter(p => p.estado === 'pendiente_pago').length
  const ahorroAnio = Number(ahorro?.ahorro_total_anio || ahorro?.ahorro_total || 0)

  function nProductos(p) {
    if (p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion') {
      return Array.isArray(p.items) ? p.items.length : 0
    }
    if (p.kit_codigo) return null  // no sabemos sin join
    return null
  }

  return (
    <ClienteLayout title="Mis Pedidos">
      <div className="pe-wrap">
        <div className="pe-headline">
          <h1>Mis Pedidos</h1>
          <p>Histórico completo: suscripciones cobradas y compras sueltas.</p>
        </div>

        {/* KPIs */}
        <div className="pe-kpis">
          <div className="pe-kpi">
            <div className="pe-kpi-label">Total pedidos</div>
            <div className="pe-kpi-num">{totalCount}</div>
            <div className="pe-kpi-sub">desde tu primera compra</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Acumulado</div>
            <div className="pe-kpi-num">{fmtMonto(totalAcumulado)}</div>
            <div className="pe-kpi-sub">IVA incluido</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Ahorrado vs súper</div>
            <div className="pe-kpi-num" style={{ color: 'var(--cl-green)' }}>{fmtMonto(ahorroAnio)}</div>
            <div className="pe-kpi-sub">acumulado total</div>
          </div>
          <div className="pe-kpi">
            <div className="pe-kpi-label">Pendiente de pago</div>
            <div className="pe-kpi-num" style={{ color: pendienteMonto > 0 ? '#f59e0b' : 'var(--cl-muted2)' }}>{fmtMonto(pendienteMonto)}</div>
            <div className="pe-kpi-sub">{pendienteCount} pedido{pendienteCount === 1 ? '' : 's'}</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="pe-tabs">
          <button className={`pe-tab ${filtro === 'todos' ? 'active' : ''}`} onClick={() => setFiltro('todos')}>
            Todos <span className="pe-tab-count">{pedidos.length}</span>
          </button>
          <button className={`pe-tab ${filtro === 'suscripciones' ? 'active' : ''}`} onClick={() => setFiltro('suscripciones')}>
            Suscripciones <span className="pe-tab-count">{pedidos.filter(p => p.tipo === 'suscripcion' || p.tipo === 'compra_unica').length}</span>
          </button>
          <button className={`pe-tab ${filtro === 'sueltos' ? 'active' : ''}`} onClick={() => setFiltro('sueltos')}>
            Compras sueltas <span className="pe-tab-count">{pedidos.filter(p => p.tipo === 'productos_sueltos' || p.tipo === 'adicional_suscripcion').length}</span>
          </button>
        </div>

        {/* Lista de pedidos */}
        {cargando ? (
          <div className="pe-loading">Cargando pedidos…</div>
        ) : filtrados.length === 0 ? (
          <div className="pe-empty">
            <div className="pe-empty-icon">📜</div>
            <h3>{pedidos.length === 0 ? 'Todavía no tenés pedidos' : 'No hay pedidos en este filtro'}</h3>
            <p>{pedidos.length === 0 ? 'Cuando hagas tu primera compra, vas a verla acá con todos los detalles.' : 'Probá con otro filtro.'}</p>
            {pedidos.length === 0 && (
              <Link to="/cliente/tienda" className="pe-empty-cta">Ir a la Tienda →</Link>
            )}
          </div>
        ) : (
          <div className="pe-list">
            {filtrados.map(p => {
              const estadoInfo = ESTADO_BADGE[p.estado] || { bg: '#f1f5f9', color: '#64748b', label: p.estado }
              const tipoInfo = TIPO_LABEL[p.tipo] || { titulo: p.tipo, sub: '' }
              const items = Array.isArray(p.items) ? p.items : []
              return (
                <div className="pe-card" key={p.id}>
                  <div className="pe-card-head">
                    <div className="pe-card-num">
                      <span className="pe-card-numero">{p.numero_orden}</span>
                      <span className="pe-card-tipo">· {tipoInfo.titulo}</span>
                    </div>
                    <span className="pe-card-badge" style={{ background: estadoInfo.bg, color: estadoInfo.color }}>{estadoInfo.label}</span>
                  </div>
                  <div className="pe-card-body">
                    {(() => {
                      const minis = getMiniaturas(p)
                      const totalProds = Array.isArray(p.items) && p.items.length > 0
                        ? p.items.length
                        : (p.kit_codigo && kitsMap[p.kit_codigo]?.productos?.length) || 0
                      const extra = totalProds - minis.length
                      return (
                        <div className="pe-card-imgs">
                          {minis.length === 0 && <div className="pe-card-img-empty">📦</div>}
                          {minis.map((sku, i) => (
                            <div className="pe-card-img" key={sku + i}>
                              {productosFotos[sku] ? <img src={productosFotos[sku]} alt="" /> : <span>📦</span>}
                            </div>
                          ))}
                          {extra > 0 && <div className="pe-card-img-extra">+{extra}</div>}
                        </div>
                      )
                    })()}
                    <div className="pe-card-info">
                      <div className="pe-card-fecha">
                        {p.fecha_entrega_programada ? `Entrega ${fmtFechaCorta(p.fecha_entrega_programada)}` : `Creado ${fmtFechaCorta(p.created_at)}`}
                      </div>
                      <div className="pe-card-meta">
                        {tipoInfo.sub}
                        {items.length > 0 && ` · ${items.length} producto${items.length === 1 ? '' : 's'}`}
                        {p.kit_codigo && kitsMap[p.kit_codigo] && ` · ${kitsMap[p.kit_codigo].nombre}`}
                      </div>
                    </div>
                    <div className="pe-card-monto">{fmtMonto(p.monto_total)}</div>
                  </div>
                  <div className="pe-card-actions">
                    {p.estado === 'pendiente_pago' && (
                      <Link to={`/cliente/pagar-pedido/${p.id}`} className="pe-action pe-action-primary">💳 Pagar</Link>
                    )}
                    {(p.estado === 'entregado' || p.estado === 'pagado' || p.estado === 'programada') && (
                      <button className="pe-action" onClick={() => alert('Próximamente: descargar factura')}>🧾 Factura</button>
                    )}
                    {p.estado === 'entregado' && (
                      <button className="pe-action" onClick={() => navigate('/cliente/tienda')}>↻ Repetir</button>
                    )}
                    <button className="pe-action" onClick={() => alert('Próximamente: ver detalle')}>👁️ Detalle</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        .pe-wrap { max-width: 1320px; margin: 0 auto; padding: 16px 16px 32px }
        .pe-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.02em }
        .pe-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 4px; line-height: 1.5 }

        .pe-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 18px }
        .pe-kpi { background: var(--cl-card); border: 1px solid var(--cl-line); padding: 14px; border-radius: 12px }
        .pe-kpi-label { font-size: 10px; color: var(--cl-muted2); font-weight: 800; letter-spacing: .08em; text-transform: uppercase }
        .pe-kpi-num { font-size: 20px; font-weight: 900; color: var(--cl-navy); margin-top: 4px; letter-spacing: -.02em }
        .pe-kpi-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }

        .pe-tabs {
          display: flex; gap: 8px; margin: 22px 0 14px;
          border-bottom: 1px solid var(--cl-line); padding-bottom: 0;
          overflow-x: auto;
        }
        .pe-tabs::-webkit-scrollbar { display: none }
        .pe-tab {
          flex-shrink: 0;
          background: none; border: 0; padding: 10px 4px; cursor: pointer;
          font-family: inherit; font-size: 13px; font-weight: 700;
          color: var(--cl-muted2); border-bottom: 2px solid transparent;
          margin-bottom: -1px; display: inline-flex; gap: 6px; align-items: center;
        }
        .pe-tab.active { color: var(--cl-navy); border-bottom-color: var(--cl-teal) }
        .pe-tab-count { font-size: 11px; opacity: .7; font-weight: 800 }

        .pe-loading { padding: 40px; text-align: center; color: var(--cl-muted) }
        .pe-empty { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 40px 24px; text-align: center; margin-top: 18px }
        .pe-empty-icon { font-size: 48px; margin-bottom: 12px }
        .pe-empty h3 { font-size: 18px; font-weight: 800; color: var(--cl-navy) }
        .pe-empty p { font-size: 13px; color: var(--cl-muted); margin: 8px 0 18px; line-height: 1.5 }
        .pe-empty-cta { display: inline-block; background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); color: #fff; padding: 12px 22px; border-radius: 999px; font-weight: 800; font-size: 14px; text-decoration: none }

        .pe-list { display: flex; flex-direction: column; gap: 12px }
        .pe-card { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; overflow: hidden }
        .pe-card-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px; border-bottom: 1px solid var(--cl-line2);
          gap: 8px;
        }
        .pe-card-num { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; min-width: 0 }
        .pe-card-numero { font-size: 12px; font-weight: 800; color: var(--cl-blue); letter-spacing: .04em; text-transform: uppercase }
        .pe-card-tipo { font-size: 12px; font-weight: 700; color: var(--cl-muted) }
        .pe-card-badge { font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase; flex-shrink: 0 }

        .pe-card-body { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 14px 16px }
        .pe-card-imgs { display: flex; align-items: center; flex-shrink: 0 }
        .pe-card-img {
          width: 38px; height: 46px; border-radius: 6px;
          background: linear-gradient(135deg, #e3d4be, #d4c5b1);
          border: 2px solid #fff; margin-left: -8px;
          box-shadow: 0 2px 6px rgba(0,0,0,.08); overflow: hidden;
          display: flex; align-items: center; justify-content: center; font-size: 16px;
        }
        .pe-card-img:first-child { margin-left: 0 }
        .pe-card-img img { width: 100%; height: 100%; object-fit: cover; display: block }
        .pe-card-img-empty {
          width: 38px; height: 46px; border-radius: 6px;
          background: var(--cl-line2);
          display: flex; align-items: center; justify-content: center; font-size: 18px;
        }
        .pe-card-img-extra {
          margin-left: 6px; font-size: 11px; font-weight: 800;
          color: var(--cl-muted); padding: 6px 8px;
          background: var(--cl-line2); border-radius: 999px;
        }
        .pe-card-info { flex: 1; min-width: 0 }
        .pe-card-fecha { font-size: 14px; font-weight: 800; color: var(--cl-navy) }
        .pe-card-meta { font-size: 12px; color: var(--cl-muted); margin-top: 2px }
        .pe-card-monto { font-size: 18px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.02em; flex-shrink: 0 }

        .pe-card-actions {
          display: flex; gap: 8px; padding: 10px 14px;
          border-top: 1px solid var(--cl-line2); flex-wrap: wrap;
        }
        .pe-action {
          background: none; border: 0; padding: 6px 10px;
          font-family: inherit; font-size: 12px; font-weight: 700;
          color: var(--cl-blue); cursor: pointer; border-radius: 8px;
          text-decoration: none;
        }
        .pe-action:hover { background: var(--cl-line2) }
        .pe-action-primary {
          background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal));
          color: #fff; padding: 7px 14px;
        }
        .pe-action-primary:hover { background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); opacity: .92 }

        @media (min-width: 700px) {
          .pe-kpis { grid-template-columns: repeat(4, 1fr); gap: 14px }
        }
        @media (min-width: 900px) {
          .pe-wrap { padding: 36px 44px }
          .pe-headline h1 { font-size: 32px }
          .pe-kpis { gap: 16px; margin-top: 22px }
          .pe-kpi { padding: 18px }
          .pe-kpi-num { font-size: 24px }
          .pe-kpi-sub { font-size: 12px }
          .pe-tabs { margin: 28px 0 16px; gap: 16px }
          .pe-tab { font-size: 14px }
          .pe-card-head { padding: 14px 20px }
          .pe-card-body { padding: 18px 20px }
          .pe-card-fecha { font-size: 16px }
          .pe-card-monto { font-size: 22px }
          .pe-card-actions { padding: 12px 20px }
          .pe-action { font-size: 13px }
        }
      `}</style>
    </ClienteLayout>
  )
}
